import { AssemblyStage } from './assemblyScene'
import { ASSEMBLY_BEATS, ASSEMBLY_STATIONS, assemblyFrame, assemblyPoster } from './assemblyTimeline'
import { ASSEMBLY_REGISTER_ANCHOR, PAD_REGISTER_ANCHOR, SCREEN_REGISTER_ANCHOR, STAGE_VIEWBOX } from './layout'
import { makePace, rulerAt, rulerFill } from './pace'
import { PadStage } from './padScene'
import { ScreenStage } from './screenScene'
import { SCREEN_BEATS, SCREEN_STATIONS, screenFrame, screenPoster } from './screenTimeline'
import { PAD_BEATS, PAD_STATIONS, padFrame, padPoster } from './timelines'
import { writeFrame } from './writer'

/* ==========================================================================
   Variant table. Every variant is a looping film on the machine kit: a stage,
   a pace (how long each part of the film lasts in the loop) and the
   station marks of its ruler. `onFrame` is the loop's frame callback and
   `drawPoster` draws the still (reduced motion, before a first play).
   ========================================================================== */

/* [film progress at the end of a segment, its duration in ms]. */
const PAD_PACE = makePace([
  [0.06, 700], // establishing shot; the last loop's register clears
  [0.14, 900], // the cup strokes off the inked etching
  [0.345, 2000], // pick: approach, squash and roll, dwell, peel
  [0.48, 1300], // shuttle to the part
  [0.52, 350], // settle
  [0.74, 2000], // print
  [0.86, 1500], // into register
  [1, 1500], // home, the whole set in shot
])

const SCREEN_PACE = makePace([
  [0.07, 1000], // out of the stencil, the flood bar drops
  [0.2, 1300], // flood stroke
  [0.295, 1100], // screen down to the off-contact gap
  [0.62, 2600], // print stroke
  [0.74, 1300], // snap-off, the screen lifts
  [0.86, 1800], // into register
  [1, 1300], // onto the printed scale
])

const ASSEMBLY_PACE = makePace([
  [0.05, 700], // out of the next cap's mark
  [0.18, 1300], // the gripper picks the cap
  [0.36, 1700], // carry and align
  [0.53, 2200], // press in, ride, click
  [0.7, 1500], // release, the nest shuttles to the probe
  [0.84, 1500], // probe and check
  [0.94, 1500], // into register
  [1, 700], // onto the assembled cap
])

function variant({ Stage, frame, poster, pace, stations, beats, register, startAt = 0 }) {
  return {
    Stage,
    viewBox: STAGE_VIEWBOX,
    register,
    duration: pace.duration,
    // The first loop opens where the film is first legible.
    startAt: pace.loopQ(startAt),
    beats: beats.map((p) => rulerAt(p, stations)),
    onFrame: (q, cycle, el) => {
      const p = pace.filmP(q)
      writeFrame(el, { ...frame(p, cycle), fill: rulerFill(q, p, stations) })
    },
    drawPoster: (el) => writeFrame(el, { ...poster(), fill: [0, 1] }),
  }
}

export const VARIANTS = {
  pad: variant({
    Stage: PadStage,
    frame: padFrame,
    poster: padPoster,
    pace: PAD_PACE,
    stations: PAD_STATIONS,
    beats: PAD_BEATS,
    register: PAD_REGISTER_ANCHOR,
  }),
  screen: variant({
    Stage: ScreenStage,
    frame: screenFrame,
    poster: screenPoster,
    pace: SCREEN_PACE,
    stations: SCREEN_STATIONS,
    beats: SCREEN_BEATS,
    register: SCREEN_REGISTER_ANCHOR,
    startAt: 0.07,
  }),
  assembly: variant({
    Stage: AssemblyStage,
    frame: assemblyFrame,
    poster: assemblyPoster,
    pace: ASSEMBLY_PACE,
    stations: ASSEMBLY_STATIONS,
    beats: ASSEMBLY_BEATS,
    register: ASSEMBLY_REGISTER_ANCHOR,
    startAt: 0.05,
  }),
}

/* Dev only: draw any frame of a paused film from the console or a capture
   script — window.__ppDraw(figure, q, cycle). Stripped from production. */
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__ppDraw = (el, q, cycle = 1) => VARIANTS[el.dataset.variant]?.onFrame(q, cycle, el)
}
